import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { strings } from '/imports/strings';
import { useMethod, useUser, useFind, useSubscribed } from '/imports/hooks';
import { Publications } from '/imports/publications';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Icon } from '/imports/ui/Icon';
import { Select } from '/imports/ui/Select';
import { css } from '/imports/ui/css';
import { Roles, Account, listAccountsForOrg } from '/imports/api/accounts';
import { Orgs } from '/imports/api/orgs';

const RolesPageStyled = styled(Page)`
  /* The body is the scrollport (both axes) and the heading stays put above it, so a matrix's sticky header
     pins to the top of the body. A wrapper that scrolled on its own would trap the sticky inside a box
     that never moves vertically. */
  .page-body { height: 0; overflow: auto; }
  .matrix-section { margin-bottom: 2.5rem; }
  .matrix-help { color: ${css.grey4}; font-size: 0.85rem; margin: 0 0 0.75rem; max-width: 60rem; }
  .matrix-legend { display: flex; flex-wrap: wrap; gap: 1.25rem; margin: 0 0 0.9rem; font-size: 0.8rem; color: ${css.grey4}; }
  .matrix-legend .legend-item { display: flex; align-items: center; gap: 0.35rem; }
  .matrix-legend .icon svg { width: 1.1rem; height: 1.1rem; }
  .matrix-legend .yes svg { fill: ${css.green2}; }
  .matrix-legend .and svg { fill: ${css.orange}; }
  .matrix-legend .or svg { fill: ${css.primaryColor}; }
  /* Grows to the matrix (cells never wrap) so the border encloses a wide one while the body scrolls sideways. */
  .matrix-scroll { width: max-content; min-width: 100%; box-sizing: border-box; border: ${css.border}; border-radius: ${css.inputBorderRadius}; }
  table.matrix { border-collapse: collapse; width: 100%; font-size: 0.85rem; }
  table.matrix th, table.matrix td { padding: 0.5rem 0.65rem; text-align: center; white-space: nowrap; border-bottom: 1px solid ${css.grey8}; }
  table.matrix thead th { position: sticky; top: 0; z-index: 1; background: ${css.grey8}; font-weight: 600; }
  table.matrix th.rowhead, table.matrix td.rowhead { text-align: left; position: sticky; left: 0; background: ${css.white}; z-index: 1; }
  table.matrix thead th.rowhead { z-index: 2; background: ${css.grey8}; }
  table.matrix tr.group td { text-align: left; font-weight: 600; background: ${css.grey9}; color: ${css.grey2}; text-transform: uppercase; font-size: 0.72rem; letter-spacing: 0.04em; }
  table.matrix .icon svg { width: 1.1rem; height: 1.1rem; }
  table.matrix .yes svg { fill: ${css.green2}; }
  table.matrix .and svg { fill: ${css.orange}; }
  table.matrix .or svg { fill: ${css.primaryColor}; }
  .su-col, .all-col { background: ${css.grey8}; }
  .org-filter { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1rem; max-width: 22rem; }
  .empty { padding: 1.5rem; color: ${css.grey4}; }
`;

type Rule =
  | { kind: 'any' }
  | { kind: 'su' }
  | { kind: 'role', role: string }
  | { kind: 'anyOf', roles: string[] }
  // `anyOf` alongside `roles` is an OR nested in the AND: every role in `roles`, plus ONE of `anyOf`.
  // Two roles grant the same thing (accounting and invoiceApprover), and a flat 'all' would read as
  // needing both.
  | { kind: 'all', roles: string[], anyOf?: string[] };

interface CapabilityRow { group: string, feature: string, rule: Rule }

// The columns are the assignable account roles plus the Super User flag (which is not an account role,
// but unlocks everything and so gets its own column).
const SU = 'su';
const roleColumns: { key: string, label: string }[] = [
  { key: SU, label: strings.RoleSuperUser() },
  { key: Roles.admin, label: strings.Admin() },
  { key: Roles.prospector, label: strings.RoleProspector() },
  { key: Roles.generate, label: strings.Generate() },
  { key: Roles.ricExecutive, label: strings.RoleRicExecutive() },
  { key: Roles.projectManagement, label: strings.RoleProjectManagement() },
  { key: Roles.pmDocumentApprover, label: strings.RolePmDocumentApprover() },
  { key: Roles.accounting, label: strings.RoleAccounting() },
  { key: Roles.invoiceApprover, label: strings.RoleInvoiceApprover() },
  { key: Roles.inventory, label: strings.Inventory() },
  { key: Roles.documents, label: strings.DocumentManagement() },
  { key: Roles.productionMeters, label: strings.RoleProductionMeters() },
  { key: Roles.consumptionMeters, label: strings.RoleConsumptionMeters() },
  { key: Roles.utilityAccounts, label: strings.UtilityAccounts() },
  { key: Roles.calpine, label: strings.RoleCalpine() },
  { key: Roles.resigen, label: strings.ResiGen() },
  { key: Roles.luminia, label: strings.RoleLuminia() },
];

// The capability matrix adds a trailing "All users" column (features open to every signed-in user). The
// account matrix uses roleColumns only — no account "holds" an all-users role.
const ALL = 'all';
const capabilityColumns = [...roleColumns, { key: ALL, label: strings.AllUsers() }];

/** Shading for the two non-role columns (Super User, All users) so they read as special. */
const colClass = (key: string) => (key === SU ? 'su-col' : key === ALL ? 'all-col' : '');

// Curated from the gates in Nav.tsx and the module guards (projectManagementIsAdmin = admin AND
// projectManagement; Data Export = both meter roles; inventory is its own grant). Grouped to match nav.
const capabilityRows: CapabilityRow[] = [
  { group: strings.Dashboard(), feature: strings.Ric(), rule: { kind: 'role', role: Roles.ricExecutive } },

  { group: strings.ProjectDevelopment(), feature: strings.Prospector(), rule: { kind: 'role', role: Roles.prospector } },
  // hasGenerate: admin is in here because an org admin has always carried the whole-org project read —
  // in listProjectsScope it widens a generate holder's scope from their own assignments to the org.
  { group: strings.ProjectDevelopment(), feature: strings.Generate(), rule: { kind: 'anyOf', roles: [Roles.generate, Roles.luminia, Roles.admin] } },
  { group: strings.ProjectDevelopment(), feature: strings.ResiGen(), rule: { kind: 'role', role: Roles.resigen } },

  { group: strings.InventoryManagement(), feature: strings.InventoryRoleView(), rule: { kind: 'role', role: Roles.inventory } },
  { group: strings.InventoryManagement(), feature: strings.InventoryRoleEdit(), rule: { kind: 'all', roles: [Roles.inventory, Roles.admin] } },
  { group: strings.InventoryManagement(), feature: strings.InventoryRoleFinance(), rule: { kind: 'all', roles: [Roles.inventory], anyOf: [Roles.accounting, Roles.invoiceApprover] } },

  { group: strings.DocumentManagement(), feature: strings.DocumentsRoleView(), rule: { kind: 'role', role: Roles.documents } },
  { group: strings.DocumentManagement(), feature: strings.DocumentsRoleAdmin(), rule: { kind: 'all', roles: [Roles.documents, Roles.admin] } },

  { group: strings.DataHub(), feature: strings.ProductionData(), rule: { kind: 'role', role: Roles.productionMeters } },
  { group: strings.DataHub(), feature: strings.ConsumptionData(), rule: { kind: 'role', role: Roles.consumptionMeters } },
  { group: strings.DataHub(), feature: strings.UtilityAccounts(), rule: { kind: 'role', role: Roles.utilityAccounts } },
  { group: strings.DataHub(), feature: strings.DataExport(), rule: { kind: 'all', roles: [Roles.productionMeters, Roles.consumptionMeters] } },

  { group: strings.ProjectManagement(), feature: strings.ProjectsAndTasks(), rule: { kind: 'role', role: Roles.projectManagement } },
  // Finance visibility (projectManagementCanSeeFinances): an accounting role paired with PM — either
  // accounting or invoiceApprover, which carries accounting's grants as well as the approval.
  { group: strings.ProjectManagement(), feature: strings.Budget(), rule: { kind: 'all', roles: [Roles.projectManagement], anyOf: [Roles.accounting, Roles.invoiceApprover] } },
  // The pipeline's Finances view and the money tiles in both PM banners: the org-wide cash roll-up, narrowed
  // 2026-10-20 to the people who authorise payments. Accounting reads the same figures per project on Budget.
  { group: strings.ProjectManagement(), feature: strings.Finances(), rule: { kind: 'role', role: Roles.invoiceApprover } },
  { group: strings.ProjectManagement(), feature: strings.Invoices(), rule: { kind: 'all', roles: [Roles.projectManagement], anyOf: [Roles.accounting, Roles.invoiceApprover] } },
  // projectManagementCanApproveInvoices: the one thing invoiceApprover adds over accounting.
  { group: strings.ProjectManagement(), feature: strings.InvoiceApproval(), rule: { kind: 'all', roles: [Roles.invoiceApprover, Roles.projectManagement] } },
  // projectManagementCanApproveDocuments: the admin tier plus its own grant. An org admin does not approve.
  { group: strings.ProjectManagement(), feature: strings.DocumentApproval(), rule: { kind: 'all', roles: [Roles.admin, Roles.projectManagement, Roles.pmDocumentApprover] } },
  { group: strings.ProjectManagement(), feature: strings.CreateProjects(), rule: { kind: 'all', roles: [Roles.admin, Roles.projectManagement, Roles.luminia] } },
  { group: strings.ProjectManagement(), feature: strings.OptionalRequirements(), rule: { kind: 'all', roles: [Roles.projectManagement, Roles.luminia] } },
  { group: strings.ProjectManagement(), feature: strings.Team(), rule: { kind: 'all', roles: [Roles.admin, Roles.projectManagement] } },
  { group: strings.ProjectManagement(), feature: strings.Portfolios(), rule: { kind: 'su' } },
  { group: strings.ProjectManagement(), feature: strings.Settings(), rule: { kind: 'su' } },

  { group: strings.Billing(), feature: strings.SolarBills(), rule: { kind: 'su' } },
  { group: strings.Billing(), feature: strings.CeaBills(), rule: { kind: 'role', role: Roles.calpine } },

  { group: strings.System(), feature: strings.Messages(), rule: { kind: 'any' } },
  // Any user may submit a ticket (create → authGuard); administering tickets is super-user only
  // (update/delete/reply → superUserGuard).
  { group: strings.System(), feature: strings.Support(), rule: { kind: 'any' } },
  { group: strings.System(), feature: strings.SupportAdmin(), rule: { kind: 'su' } },
  { group: strings.System(), feature: strings.Accounts(), rule: { kind: 'role', role: Roles.admin } },
  { group: strings.System(), feature: strings.AccountAndPreferences(), rule: { kind: 'any' } },
  { group: strings.System(), feature: strings.ApplicationSettings(), rule: { kind: 'su' } },
];

/** '' = no access, 'yes' = a role that alone grants it, 'and' = one of several required together,
 *  'or' = one of a set of interchangeable roles, held alongside the 'and' ones. */
type CellState = '' | 'yes' | 'and' | 'or';
const cellState = (rule: Rule, key: string): CellState => {
  if (key === SU) return 'yes'; // Super User unlocks everything.
  if (key === ALL) return rule.kind === 'any' ? 'yes' : ''; // "All users" checks only ungated features.
  switch (rule.kind) {
    case 'role': return rule.role === key ? 'yes' : '';
    case 'anyOf': return rule.roles.includes(key) ? 'yes' : '';
    case 'all': return rule.roles.includes(key) ? 'and' : (rule.anyOf?.includes(key) ? 'or' : '');
    default: return ''; // 'su' and 'any' are not gated on account roles.
  }
};

const Check = ({ state }: { state: CellState }) => (state ? <Icon className={state} icon="check" /> : null);

/** Super-user-only overview: which roles unlock which features, and which accounts hold which roles. */
export const RolesPage = () => {
  const user = useUser();
  const orgsLoading = useSubscribed(Publications.listMyOrgs);
  const orgs = useFind(() => Orgs.find(), [orgsLoading]);
  const [orgId, setOrgId] = useState<string | undefined>(user?.orgId);
  const activeOrgId = orgId || user?.orgId;
  const [accounts, loading] = useMethod(listAccountsForOrg, activeOrgId as string, [activeOrgId]);
  if (!user?.isSuperUser) return <NotPermittedPage />;

  const sorted = [...(accounts || [])].sort((a, b) => (a._user?.username || '').localeCompare(b._user?.username || ''));

  return (
    <RolesPageStyled className={`page${loading ? ' initializing' : ''}`}>
      <PageHeading left={<h1>{strings.RolesAndAccess()}</h1>} />
      <PageBody>
        <div className="matrix-section">
          <h2>{strings.RoleCapabilities()}</h2>
          <p className="matrix-help">{strings.RolesMatrixHelp()}</p>
          <div className="matrix-legend">
            <span className="legend-item"><Icon className="yes" icon="check" />{strings.LegendGrantsAlone()}</span>
            <span className="legend-item"><Icon className="and" icon="check" />{strings.LegendAndGate()}</span>
            <span className="legend-item"><Icon className="or" icon="check" />{strings.LegendOrGate()}</span>
          </div>
          <div className="matrix-scroll">
            <table className="matrix">
              <thead>
                <tr>
                  <th className="rowhead">{strings.Feature()}</th>
                  {capabilityColumns.map((c) => <th key={c.key} className={colClass(c.key)}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {capabilityRows.map((row, i) => {
                  const prev = capabilityRows[i - 1];
                  const groupHeader = !prev || prev.group !== row.group;
                  return (
                    <React.Fragment key={`${row.group}-${row.feature}`}>
                      {groupHeader && <tr className="group"><td colSpan={capabilityColumns.length + 1}>{row.group}</td></tr>}
                      <tr>
                        <td className="rowhead">{row.feature}</td>
                        {capabilityColumns.map((c) => (
                          <td key={c.key} className={colClass(c.key)}><Check state={cellState(row.rule, c.key)} /></td>
                        ))}
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="matrix-section">
          <h2>{strings.AccountAssignments()}</h2>
          <p className="matrix-help">{strings.SuperUserNotAccountRole()}</p>
          {orgs.length > 1 && (
            <div className="org-filter">
              <span>{strings.Organization()}:</span>
              <Select
                value={activeOrgId}
                options={orgs.map((o) => ({ value: o._id, label: o.name || o._id }))}
                onChange={(v) => setOrgId(v)}
              />
            </div>
          )}
          <div className="matrix-scroll">
            <table className="matrix">
              <thead>
                <tr>
                  <th className="rowhead">{strings.Username()}</th>
                  {roleColumns.map((c) => <th key={c.key} className={colClass(c.key)}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {sorted.map((a: Account) => (
                  <tr key={a._id}>
                    <td className="rowhead"><Link to={`/accounts/${a._id}`}>{a._user?.username || `${a.firstName} ${a.lastName}`}</Link></td>
                    {roleColumns.map((c) => {
                      const held = c.key === SU ? !!a._user?.isSuperUser : !!a.roles?.includes(c.key);
                      return <td key={c.key} className={colClass(c.key)}>{held ? <Icon className="yes" icon="check" /> : null}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && !sorted.length && <div className="empty">{strings.NoAccounts()}</div>}
        </div>
      </PageBody>
    </RolesPageStyled>
  );
};
